import clsx from 'clsx';
import * as React from 'react';

import { type InlineLinkData, selectInline } from '@gitbook/doc-core';
import type { InlineProps } from '@gitbook/slate-react';
import { useIsEditorFocused } from '@gitbook/slate-react';

import { type UIResolvedContentReference, useContentReference } from '../../ContentReferences';
import { EditLinkWrapper } from './EditLinkWrapper';
import { useInlineLinkProps } from './useInlineLinkProp';

/**
 * Component to render a link node.
 */
export function InlineLink(props: InlineProps): React.ReactElement {
    return (
        <React.Suspense fallback={<InlineLinkImplementation {...props} resolved={null} />}>
            <ResolvedInlineLink {...props} />
        </React.Suspense>
    );
}

function ResolvedInlineLink(props: InlineProps) {
    const { node } = props;
    const { ref } = node.data.toFrozenObject<InlineLinkData>();
    const resolved = useContentReference(ref, { resolveAnchorText: false });

    return <InlineLinkImplementation {...props} resolved={resolved} />;
}

/**
 * The actual implementation of the link. Depending on whether we have resolved
 * the content reference, it will be a proper link or just something that looks like it.
 */
function InlineLinkImplementation(
    props: InlineProps & { resolved: UIResolvedContentReference | null }
): React.ReactElement {
    const { node, parent, attributes, children, resolved, editor } = props;
    const { readOnly } = editor;

    const isUnresolvedRef = Boolean(resolved && !resolved.resolution);
    const unresolvedRefClassName = clsx(
        '-top-[0.03125rem] relative mx-0.25 inline-flex cursor-default items-center gap-1 text-wrap rounded bg-[var(--sp-color-bg-mention-default)] px-1 align-middle text-primary text-ui-base leading-6 no-underline'
    );
    const isEditorFocused = useIsEditorFocused();

    const inlineLinkProps = useInlineLinkProps(resolved);

    const anchor = (
        <a
            {...attributes}
            onClick={
                inlineLinkProps?.onClick
                    ? (event) => {
                          const shouldOpenLink = !isEditorFocused || event.ctrlKey || event.metaKey;
                          if (shouldOpenLink) {
                              if (inlineLinkProps?.onClick) {
                                  inlineLinkProps.onClick(event);
                              }
                          } else {
                              event.preventDefault();
                              editor.change((change) => selectInline(change, node));
                          }
                      }
                    : undefined
            }
            href={inlineLinkProps?.href}
            target={inlineLinkProps?.target}
            aria-busy={resolved === null ? 'true' : undefined}
            data-link-broken={isUnresolvedRef}
            className={clsx(
                isUnresolvedRef
                    ? unresolvedRefClassName
                    : 'align-baseline text-primary underline underline-offset-2 hover:no-underline',
                !isUnresolvedRef ? (isEditorFocused ? 'cursor-default' : 'cursor-pointer') : null
            )}
        >
            {resolved?.secondaryIcon && isUnresolvedRef ? (
                <resolved.secondaryIcon className="align-[-12%]" />
            ) : null}

            {children}
        </a>
    );

    if (readOnly || !resolved) {
        return anchor;
    }

    return (
        <EditLinkWrapper
            resolved={resolved}
            editor={editor}
            node={node}
            parent={parent}
            linkProps={inlineLinkProps}
        >
            {anchor}
        </EditLinkWrapper>
    );
}
